import { message } from 'antd';

// 分批下载配置
export interface BatchDownloadConfig {
    batchSize: number;
    maxConcurrent: number;
    delayBetweenBatches: number;
}

// 默认配置
export const DEFAULT_BATCH_CONFIG: BatchDownloadConfig = {
    batchSize: 1000,      // 每批下载1000条数据
    maxConcurrent: 3,     // 最大并发请求数
    delayBetweenBatches: 200  // 批次间延迟(ms)
};

// 分批下载服务
export class BatchDownloadService {
    
    private config: BatchDownloadConfig;
    
    constructor(config: BatchDownloadConfig = DEFAULT_BATCH_CONFIG) {
        this.config = config;
    }
    
    // 延迟函数
    private delay(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }
    
    // 分批并行下载函数 - 从后端分批获取数据，前端合并成一个大数组
    async downloadInBatches<T>(
        totalCount: number,
        fetchBatch: (page: number, size: number) => Promise<T[]>
    ): Promise<T[]> {
        const totalPages = Math.ceil(totalCount / this.config.batchSize);
        const allMergedData: T[] = []; // 用于存储所有批次合并后的数据
        
        message.info(`开始分批下载，共 ${totalPages} 批次，每批 ${this.config.batchSize} 条数据`);
        
        // 创建批次任务
        const batches: Array<() => Promise<T[]>> = [];
        for (let page = 1; page <= totalPages; page++) {
            batches.push(() => fetchBatch(page, this.config.batchSize));
        }

        // 并行执行批次，控制并发数，并实时合并数据
        const executeWithConcurrency = async (tasks: Array<() => Promise<T[]>>, concurrency: number) => {
            
            for (let i = 0; i < tasks.length; i += concurrency) {
                const currentBatch = tasks.slice(i, i + concurrency);
                
                // 并行执行当前批次
                const batchResults = await Promise.all(
                    currentBatch.map(async (task, index) => {
                        try {
                            const result = await task();
                            const actualIndex = i + index + 1;
                            message.info(`批次 ${actualIndex}/${totalPages} 下载完成 (${result.length} 条数据)`);
                            return result;
                        } catch (error) {
                            console.error(`批次 ${i + index + 1} 下载失败:`, error);
                            message.error(`批次 ${i + index + 1} 下载失败，正在重试...`);
                            // 简单重试一次
                            await this.delay(1000);
                            return await task();
                        }
                    })
                );
                
                // 将当前批次的数据合并到总数组中
                batchResults.forEach(batchData => {
                    allMergedData.push(...batchData);
                });
                
                // 显示当前合并进度
                message.info(`已合并 ${allMergedData.length} 条数据，进度：${Math.round(allMergedData.length / totalCount * 100)}%`);
                
                // 批次间稍作延迟，避免服务器压力过大
                if (i + concurrency < tasks.length) {
                    await this.delay(this.config.delayBetweenBatches);
                }
            }
            
            return allMergedData;
        };

        const mergedData = await executeWithConcurrency(batches, this.config.maxConcurrent);
        message.success(`所有批次下载完成！总共获取了 ${mergedData.length} 条数据，正在生成Excel文件...`);
        
        return mergedData;
    }
    
    // 检查是否需要分批下载
    shouldUseBatchDownload(totalCount: number): boolean {
        return totalCount > this.config.batchSize;
    }
    
    // 获取总批次数
    getTotalBatches(totalCount: number): number {
        return Math.ceil(totalCount / this.config.batchSize);
    }
} 